import type { MouseEvent } from 'react';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

type ConfirmModalProps = Readonly<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Defaults to "Confirm". Pass a translated string for i18n. */
  confirmLabel?: string;
  /** Defaults to "Cancel". Pass a translated string for i18n. */
  cancelLabel?: string;
  /**
   * Runs when Confirm is clicked. Clicking Confirm never closes the dialog by itself —
   * the caller closes it by setting `open` to false once the action succeeds, and leaves
   * it open on failure so the user can retry. Async handlers are awaited.
   */
  onConfirm: () => void | Promise<void>;
  /** While true: spinner on Confirm, both buttons disabled, and Escape is ignored. */
  isLoading?: boolean;
  variant?: 'default' | 'destructive';
}>;

const ConfirmModal = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  isLoading = false,
  variant = 'default',
}: ConfirmModalProps): JSX.Element => {
  const handleOpenChange = (nextOpen: boolean) => {
    // Block Escape/programmatic close while the action is in flight; the caller owns close-on-success.
    if (nextOpen || !isLoading) {
      onOpenChange(nextOpen);
    }
  };

  const handleConfirm = async (event: MouseEvent<HTMLButtonElement>) => {
    // Radix's AlertDialog.Action is a Dialog.Close: it closes the dialog unless the click is default-prevented.
    event.preventDefault();
    await onConfirm();
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            variant={variant === 'destructive' ? 'destructive' : 'default'}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmModal;
